// Frontend mirror of backend/src/lib/scene-time-of-day.js — keep both in sync. Pure functions; reads scene
// headings, never writes. Manual Schedule warnings do not use this module.
export const isTime = t => /^([01]\d|2[0-3]):[0-5]\d$/.test(String(t || ''));
export const toMin = t => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
export const toTime = m => { const v = ((m % 1440) + 1440) % 1440; return `${String(Math.floor(v / 60)).padStart(2, '0')}:${String(v % 60).padStart(2, '0')}`; };

// INT. / EXT. / INT./EXT. / EXT./INT. / I/E prefixes.
export const settingOf = (heading = '') => {
  const h = String(heading).trim().toUpperCase();
  if (/^(INT\.?\s*\/\s*EXT|EXT\.?\s*\/\s*INT|I\s*\/\s*E\b)/.test(h)) return 'INT/EXT';
  if (/^EXT\b/.test(h)) return 'EXT';
  if (/^INT\b/.test(h)) return 'INT';
  return null;
};
// Required time of day from the heading's time segment (after the last " - ").
export const timeOfDayOf = (heading = '', time = '') => {
  const parts = String(heading).toUpperCase().split(/\s+[-–—]\s+/);
  const src = `${String(time || '').toUpperCase()} ${parts.length > 1 ? parts.slice(1).join(' ') : ''}`;
  if (/\bDAWN|SUNRISE|DAYBREAK\b/.test(src)) return 'DAWN';
  if (/\bDUSK|SUNSET|TWILIGHT\b/.test(src)) return 'DUSK';
  if (/\bEVENING\b/.test(src)) return 'EVENING';
  if (/\bNIGHT\b/.test(src)) return 'NIGHT';
  if (/\bDAY|MORNING|AFTERNOON|NOON\b/.test(src)) return 'DAY';
  return null;
};
export const RANK = { DAWN: 0, DAY: 1, DUSK: 3, EVENING: 4, NIGHT: 5 };
const needsRealLight = s => s === 'EXT' || s === 'INT/EXT';
export const describe = scene => {
  const setting = scene?.setting || settingOf(scene?.heading) || (scene?.intExt === 'EXT' ? 'EXT' : scene?.intExt === 'INT' ? 'INT' : null);
  const timeOfDay = timeOfDayOf(scene?.heading, scene?.time);
  return { setting, timeOfDay, lightingFlexible: !needsRealLight(setting) };
};

// Sunrise/sunset: actual values only when supplied for the day; otherwise clearly assumed.
export const DEFAULT_SUN = { sunrise: '06:00', sunset: '18:00', assumed: true };
export const sunFor = (day = {}) => (isTime(day.sunrise) && isTime(day.sunset) ? { sunrise: day.sunrise, sunset: day.sunset, assumed: false } : DEFAULT_SUN);
// Windows in minutes from midnight; NIGHT crosses midnight.
export const windowFor = (timeOfDay, sun = DEFAULT_SUN) => {
  const r = toMin(sun.sunrise); const s = toMin(sun.sunset);
  switch (timeOfDay) {
    case 'DAWN': return { from: r - 60, to: r + 60 };
    case 'DAY': return { from: r, to: s };
    case 'DUSK': return { from: s - 60, to: s + 30 };
    case 'EVENING': return { from: s, to: s + 180 };
    case 'NIGHT': return { from: s + 30, to: r - 30 + 1440 };
    default: return null;
  }
};
const inWindow = (m, w) => { for (const k of [-1440, 0, 1440]) if (m >= w.from + k && m <= w.to + k) return true; return false; };
export const fits = (startMin, duration, w) => { for (let m = startMin; m <= startMin + Math.max(0, duration); m += 5) if (!inWindow(m, w)) return false; return true; };

// Generation: keep location blocks together, order blocks DAWN → DAY → interiors → DUSK →
// EVENING → NIGHT, and order exterior scenes inside a block the same way (stable).
export const arrangeByTimeOfDay = placed => {
  const blocks = [];
  for (const p of placed) { const last = blocks[blocks.length - 1]; if (last && last.location === p.location) last.items.push(p); else blocks.push({ location: p.location, items: [p] }); }
  const rankOf = p => { const d = describe(p); return !d.lightingFlexible && d.timeOfDay ? RANK[d.timeOfDay] : null; };
  const ranked = blocks.map((b, i) => {
    const rs = b.items.map(rankOf).filter(r => r !== null);
    const items = b.items.map((p, j) => ({ p, j, r: rankOf(p) ?? (rs.length ? Math.min(...rs) : 2) })).sort((a, c) => a.r - c.r || a.j - c.j).map(x => x.p);
    return { i, r: rs.length ? Math.min(...rs) : 2, items };
  });
  return ranked.sort((a, b) => a.r - b.r || a.i - b.i).flatMap(b => b.items);
};
// A day whose real-light scenes all need darkness starts so that the first scene meets nightfall.
export const nightOnly = placed => {
  const real = placed.map(describe).filter(d => !d.lightingFlexible && d.timeOfDay);
  return placed.length > 0 && real.length === placed.length && real.every(d => ['NIGHT', 'EVENING'].includes(d.timeOfDay));
};

// Warnings and recommendations for exterior and INT./EXT. scenes outside their window.
// Interiors are flexible: lighting can simulate the time of day, so they are never flagged.
export const timeOfDayWarnings = (days = [], sceneOf = () => null) => {
  const out = [];
  for (const d of days) {
    const sun = sunFor(d); let offset = 0; let prev = null;
    for (const e of d.entries || []) {
      if (!isTime(e.start)) continue;
      let at = toMin(e.start) + offset; if (prev !== null && at < prev && prev - at > 720) { offset += 1440; at += 1440; }
      prev = at;
      if (e.kind !== 'scene') continue;
      const scene = sceneOf(e.sceneUid); if (!scene) continue;
      const { setting, timeOfDay, lightingFlexible } = describe(scene);
      if (lightingFlexible || !timeOfDay) continue;
      const w = windowFor(timeOfDay, sun);
      if (fits(at, e.durationMin || 0, w)) continue;
      const label = timeOfDay === 'DUSK' ? 'DUSK / SUNSET' : timeOfDay;
      out.push({ type: 'TIME_OF_DAY', dayNumber: d.dayNumber, entryId: e.id, sceneUid: e.sceneUid,
        message: `Day ${d.dayNumber}: Scene ${scene.number ?? ''} (${setting} ${label}) is scheduled ${toTime(at)}–${toTime(at + (e.durationMin || 0))}, outside its ${label} window (${toTime(w.from)}–${toTime(w.to)}). Recommendation: move it ${timeOfDay === 'NIGHT' || timeOfDay === 'EVENING' ? 'to a night shoot or the end of the day' : timeOfDay === 'DAWN' ? 'to start at first light' : timeOfDay === 'DUSK' ? 'to the sunset window' : 'into daylight hours'}, or plan lighting cover.${sun.assumed ? ' Sunrise/sunset assumed 06:00/18:00 — actual times for this location and date are not available.' : ''}` });
    }
  }
  return out;
};

